<script setup>
import { ref, computed, getCurrentInstance } from 'vue';
import { Head, Link, useForm } from '@inertiajs/vue3';
import { useToast } from 'primevue/usetoast';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import Card from 'primevue/card';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Dropdown from 'primevue/dropdown';
import InputNumber from 'primevue/inputnumber';
import InputSwitch from 'primevue/inputswitch';
import Calendar from 'primevue/calendar';
import Message from 'primevue/message';

defineOptions({
    layout: AdminMainLayout,
});

const props = defineProps({
    banner: Object,
    layoutOptions: Array,
    audienceOptions: Array,
    targetOptions: Array,
    displayModeOptions: Array,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();

const form = useForm({
    title: props.banner?.title || '',
    subtitle: props.banner?.subtitle || '',
    display_mode: props.banner?.display_mode || 'inline',
    body: props.banner?.body || '',
    image_url: props.banner?.image_url || '',
    alt_text: props.banner?.alt_text || '',
    cta_label: props.banner?.cta_label || '',
    cta_url: props.banner?.cta_url || '',
    layout: props.banner?.layout || 'image-bg',
    background_color: props.banner?.background_color || '',
    text_color: props.banner?.text_color || '',
    audience: props.banner?.audience || 'both',
    targets: props.banner?.targets || 'both',
    dismissible: false, // Always false for both CREATE and EDIT
    is_active: props.banner?.is_active ?? true,
    priority: props.banner?.priority || 100,
    weight: props.banner?.weight || 100,
    max_impressions_per_user_per_day: props.banner?.max_impressions_per_user_per_day || null,
    publish_at: props.banner?.publish_at ? new Date(props.banner.publish_at) : null,
    expire_at: props.banner?.expire_at ? new Date(props.banner.expire_at) : null,
});

const isModal = computed(() => form.display_mode === 'modal');

const displayModeDropdownOptions = computed(() =>
    (props.displayModeOptions || ['inline', 'modal']).map(mode => ({
        label: mode === 'modal' ? $t('Pop-up modal') : $t('Dashboard card'),
        value: mode,
    }))
);

const layoutDropdownOptions = computed(() =>
    props.layoutOptions.map(layout => ({
        label: layout.charAt(0).toUpperCase() + layout.slice(1).replace('-', ' '),
        value: layout,
    }))
);

const audienceDropdownOptions = computed(() =>
    props.audienceOptions.map(audience => ({
        label: audience.charAt(0).toUpperCase() + audience.slice(1),
        value: audience,
    }))
);

const targetDropdownOptions = computed(() =>
    props.targetOptions.map(target => ({
        label: target.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        value: target,
    }))
);

function submit() {
    const formData = {
        ...form.data(),
        publish_at: form.publish_at ? new Date(form.publish_at).toISOString() : null,
        expire_at: form.expire_at ? new Date(form.expire_at).toISOString() : null,
    };

    if (props.banner) {
        form.transform(() => formData).put(route('content.banners.update', props.banner.id), {
            onSuccess: () => {
                toast.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Banner updated successfully',
                    life: 3000,
                });
            },
            onError: (errors) => {
                toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to update banner. Please check the form.',
                    life: 5000,
                });
                console.error('Validation errors:', errors);
            },
        });
    } else {
        form.transform(() => formData).post(route('content.banners.store'), {
            onSuccess: () => {
                toast.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Banner created successfully',
                    life: 3000,
                });
            },
            onError: (errors) => {
                toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to create banner. Please check the form.',
                    life: 5000,
                });
                console.error('Validation errors:', errors);
            },
        });
    }
}

function previewImage() {
    if (form.image_url) {
        window.open(form.image_url, '_blank');
    }
}
</script>

<template>
    <div>
        <Head :title="banner ? $t('Edit Banner') : $t('Create Banner')" />

        <div class="grid">
            <div class="col-12">
                <Card>
                    <template #title>
                        <div class="flex flex-grow align-items-center justify-content-between">
                            <span>{{ banner ? $t('Edit Banner') : $t('Create Banner') }}</span>
                            <Link :href="route('content.banners.index')" class="ml-auto">
                                <Button :label="$t('Back to List')" icon="pi pi-arrow-left" severity="secondary" />
                            </Link>
                        </div>
                    </template>

                    <template #content>
                        <form @submit.prevent="submit">
                            <div class="grid">
<!--                                &lt;!&ndash; Basic Information &ndash;&gt;-->
<!--                                <div class="col-12 mb-2">-->
<!--                                    <h5 class="mb-0">Basic Information</h5>-->
<!--                                </div>-->

                                <div class="col-12 md:col-6">
                                    <div class="field mb-4">
                                        <label for="display_mode" class="block text-900 font-medium mb-2">{{ $t('Display Mode') }} <span class="text-red-500">*</span></label>
                                        <Dropdown
                                            id="display_mode"
                                            v-model="form.display_mode"
                                            :options="displayModeDropdownOptions"
                                            optionLabel="label"
                                            optionValue="value"
                                            class="w-full"
                                        />
                                        <small class="text-color-secondary">{{ $t('Pop-up modals appear once on any page until the user dismisses them') }}</small>
                                    </div>
                                </div>

                                <div class="col-12 md:col-6">
                                    <div class="field mb-4">
                                        <label for="title" class="block text-900 font-medium mb-2">{{ $t('Title') }} <span class="text-red-500">*</span></label>
                                        <InputText
                                            id="title"
                                            v-model="form.title"
                                            class="w-full"
                                            :class="{ 'p-invalid': form.errors.title }"
                                        />
                                        <small v-if="form.errors.title" class="p-error">
                                            {{ form.errors.title }}
                                        </small>
                                    </div>
                                </div>

                                <div v-if="!isModal" class="col-12 md:col-6">
                                    <div class="field mb-4">
                                        <label for="subtitle" class="block text-900 font-medium mb-2">{{ $t('Subtitle') }}</label>
                                        <InputText
                                            id="subtitle"
                                            v-model="form.subtitle"
                                            class="w-full"
                                        />
                                    </div>
                                </div>

                                <div v-if="isModal" class="col-12">
                                    <div class="field mb-4">
                                        <label for="body" class="block text-900 font-medium mb-2">{{ $t('Body') }} <span class="text-red-500">*</span></label>
                                        <Textarea
                                            id="body"
                                            v-model="form.body"
                                            class="w-full"
                                            rows="8"
                                            autoResize
                                            :class="{ 'p-invalid': form.errors.body }"
                                        />
                                        <small v-if="form.errors.body" class="p-error">
                                            {{ form.errors.body }}
                                        </small>
                                        <small v-else class="text-color-secondary">{{ $t('Blank lines split the text into paragraphs') }}</small>
                                    </div>
                                </div>

                                <div v-if="!isModal" class="col-12">
                                    <div class="field mb-4">
                                        <label for="image_url" class="block text-900 font-medium mb-2">{{ $t('Image URL') }} <span class="text-red-500">*</span></label>
                                        <InputText
                                            id="image_url"
                                            v-model="form.image_url"
                                            placeholder="https://example.com/image.jpg"
                                            class="w-full"
                                            :class="{ 'p-invalid': form.errors.image_url }"
                                        />
                                        <small v-if="form.errors.image_url" class="p-error">
                                            {{ form.errors.image_url }}
                                        </small>
                                    </div>
                                </div>

                                <div v-if="!isModal" class="col-12 md:col-6">
                                    <div class="field mb-4">
                                        <label for="alt_text" class="block text-900 font-medium mb-2">{{ $t('Alt Text') }} <span class="text-red-500">*</span></label>
                                        <InputText
                                            id="alt_text"
                                            v-model="form.alt_text"
                                            class="w-full"
                                            :placeholder="$t('Image description for accessibility')"
                                            :class="{ 'p-invalid': form.errors.alt_text }"
                                        />
                                        <small v-if="form.errors.alt_text" class="p-error">
                                            {{ form.errors.alt_text }}
                                        </small>
                                    </div>
                                </div>

                                <div v-if="!isModal" class="col-12 md:col-6">
                                    <div class="field mb-4">
                                        <label for="layout" class="block text-900 font-medium mb-2">{{ $t('Layout') }} <span class="text-red-500">*</span></label>
                                        <Dropdown
                                            id="layout"
                                            v-model="form.layout"
                                            :options="layoutDropdownOptions"
                                            optionLabel="label"
                                            optionValue="value"
                                            class="w-full"
                                        />
                                    </div>
                                </div>

                                <!-- CTA Settings -->
                                <div v-if="!isModal" class="col-12 mt-5 mb-2">
                                    <h5 class="mb-0">{{ $t('Call to Action') }}</h5>
                                </div>

                                <div v-if="!isModal" class="col-12 md:col-6">
                                    <div class="field mb-4">
                                        <label for="cta_label" class="block text-900 font-medium mb-2">{{ $t('CTA Button Label') }}</label>
                                        <InputText
                                            id="cta_label"
                                            v-model="form.cta_label"
                                            class="w-full"
                                            :placeholder="$t('Learn More')"
                                        />
                                    </div>
                                </div>

                                <div v-if="!isModal" class="col-12 md:col-6">
                                    <div class="field mb-4">
                                        <label for="cta_url" class="block text-900 font-medium mb-2">{{ $t('CTA URL') }}</label>
                                        <InputText
                                            id="cta_url"
                                            v-model="form.cta_url"
                                            class="w-full"
                                            :placeholder="$t('https://example.com')"
                                        />
                                    </div>
                                </div>

                                <!-- Appearance -->
                                <div v-if="!isModal" class="col-12 mt-5 mb-2">
                                    <h5 class="mb-0">{{ $t('Appearance') }}</h5>
                                </div>

                                <div v-if="!isModal" class="col-12 md:col-6">
                                    <div class="field mb-4">
                                        <label for="background_color" class="block text-900 font-medium mb-2">
                                            {{ $t('Background Color') }}
                                        </label>
                                        <InputText
                                            id="background_color"
                                            v-model="form.background_color"
                                            class="w-full"
                                            placeholder="#000000"
                                        />
                                        <small class="text-color-secondary">{{ $t('Hex color code (e.g., #1a1a1a)') }}</small>
                                    </div>
                                </div>

                                <div v-if="!isModal" class="col-12 md:col-6">
                                    <div class="field mb-4">
                                        <label for="text_color" class="block text-900 font-medium mb-2">{{ $t('Text Color') }}</label>
                                        <InputText
                                            id="text_color"
                                            v-model="form.text_color"
                                            class="w-full"
                                            placeholder="#ffffff"
                                        />
                                        <small class="text-color-secondary">{{ $t('Hex color code (e.g., #ffffff)') }}</small>
                                    </div>
                                </div>

                                <!-- Targeting -->
                                <div class="col-12 mt-5 mb-2">
                                    <h5 class="mb-0">{{ $t('Targeting') }}</h5>
                                </div>

                                <div class="col-12 md:col-4">
                                    <div class="field mb-4">
                                        <label for="audience" class="block text-900 font-medium mb-2">{{ $t('Audience') }} <span class="text-red-500">*</span></label>
                                        <Dropdown
                                            id="audience"
                                            v-model="form.audience"
                                            :options="audienceDropdownOptions"
                                            optionLabel="label"
                                            optionValue="value"
                                            class="w-full"
                                        />
                                    </div>
                                </div>

                                <div v-if="!isModal" class="col-12 md:col-4">
                                    <div class="field mb-4">
                                        <label for="targets" class="block text-900 font-medium mb-2">{{ $t('Page Targets') }} <span class="text-red-500">*</span></label>
                                        <Dropdown
                                            id="targets"
                                            v-model="form.targets"
                                            :options="targetDropdownOptions"
                                            optionLabel="label"
                                            optionValue="value"
                                            class="w-full"
                                        />
                                    </div>
                                </div>

<!--                                <div class="col-12 md:col-4">-->
<!--                                    <div class="field mb-4">-->
<!--                                        <label for="max_impressions" class="block text-900 font-medium mb-2">-->
<!--                                            Max Impressions/User/Day-->
<!--                                        </label>-->
<!--                                        <InputNumber-->
<!--                                            id="max_impressions"-->
<!--                                            v-model="form.max_impressions_per_user_per_day"-->
<!--                                            class="w-full"-->
<!--                                            :min="1"-->
<!--                                            :max="100"-->
<!--                                            placeholder="Unlimited"-->
<!--                                        />-->
<!--                                    </div>-->
<!--                                </div>-->

                                <!-- Scheduling -->
                                <div class="col-12 mt-5 mb-2">
                                    <h5 class="mb-0">{{ $t('Scheduling') }}</h5>
                                </div>

                                <div class="col-12 md:col-6">
                                    <div class="field mb-4">
                                        <label for="publish_at" class="block text-900 font-medium mb-2">{{ $t('Publish Date') }}</label>
                                        <Calendar
                                            id="publish_at"
                                            v-model="form.publish_at"
                                            showTime
                                            hourFormat="24"
                                            dateFormat="yy-mm-dd"
                                            class="w-full"
                                            :placeholder="$t('Immediately')"
                                        />
                                    </div>
                                </div>

                                <div class="col-12 md:col-6">
                                    <div class="field mb-4">
                                        <label for="expire_at" class="block text-900 font-medium mb-2">{{ $t('Expire Date') }}</label>
                                        <Calendar
                                            id="expire_at"
                                            v-model="form.expire_at"
                                            showTime
                                            hourFormat="24"
                                            dateFormat="yy-mm-dd"
                                            class="w-full"
                                            :placeholder="$t('Never')"
                                        />
                                    </div>
                                </div>

                                <!-- Priority & Settings -->
                                <div class="col-12 mt-5 mb-2">
                                    <h5 class="mb-0">{{ $t('Priority & Settings') }}</h5>
                                </div>

                                <div class="col-12 md:col-3">
                                    <div class="field mb-4">
                                        <label for="priority" class="block text-900 font-medium mb-2">{{ $t('Priority') }}</label>
                                        <InputNumber
                                            id="priority"
                                            v-model="form.priority"
                                            class="w-full"
                                            :min="0"
                                            :max="65535"
                                        />
                                        <small class="text-color-secondary">{{ $t('Higher priority shows first') }}</small>
                                    </div>
                                </div>

<!--                                <div class="col-12 md:col-3">-->
<!--                                    <div class="field mb-4">-->
<!--                                        <label for="weight" class="block text-900 font-medium mb-2">Weight</label>-->
<!--                                        <InputNumber-->
<!--                                            id="weight"-->
<!--                                            v-model="form.weight"-->
<!--                                            class="w-full"-->
<!--                                            :min="1"-->
<!--                                            :max="100"-->
<!--                                        />-->
<!--                                        <small class="text-color-secondary">For weighted random selection</small>-->
<!--                                    </div>-->
<!--                                </div>-->

<!--                                <div class="col-12 md:col-3">-->
<!--                                    <div class="field mb-4">-->
<!--                                        <label class="block text-900 font-medium mb-2">Dismissible</label>-->
<!--                                        <InputSwitch v-model="form.dismissible" />-->
<!--                                        <div class="mt-2">-->
<!--                                            <small class="text-color-secondary">Allow users to dismiss</small>-->
<!--                                        </div>-->
<!--                                    </div>-->
<!--                                </div>-->

                                <div class="col-12 md:col-3">
                                    <div class="field mb-4">
                                        <label class="block text-900 font-medium mb-2">{{ $t('Active') }}</label>
                                        <InputSwitch v-model="form.is_active" />
                                        <div class="mt-2">
                                            <small class="text-color-secondary">{{ $t('Banner is active') }}</small>
                                        </div>
                                    </div>
                                </div>

                                <!-- Actions -->
                                <div class="col-12 mt-4">
                                    <div class="flex gap-2">
                                        <Button
                                            type="submit"
                                            :label="banner ? $t('Update Banner') : $t('Create Banner')"
                                            icon="pi pi-save"
                                            severity="primary"
                                            :loading="form.processing"
                                        />
                                        <Link :href="route('content.banners.index')" class="no-underline">
                                            <Button
                                                :label="$t('Cancel')"
                                                severity="secondary"
                                                text
                                            />
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </template>
                </Card>
            </div>
        </div>
    </div>
</template>
